import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './useAuthContext';
import { Permission } from './useAuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
}

/**
 * ProtectedRoute Component
 * 
 * Protects routes that require authentication and specific permissions
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission
}) => {
  const location = useLocation();
  const { userRole, isLoading, hasPermission, isAuthenticated } = useAuthContext();
  
  console.log(`[ProtectedRoute] Path: ${location.pathname}, isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}, userRole: ${userRole}`);

  // Show loading state while auth is being checked
  if (isLoading) {
    console.log(`[ProtectedRoute] Path: ${location.pathname} - Rendering Loading Spinner`);
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log(`[ProtectedRoute] Path: ${location.pathname} - Not authenticated, redirecting to login`);
    
    // If we're on the portal domain, redirect to accounts domain
    if (window.location.hostname === 'portal.recurringvaluepodcast.com') {
      window.location.href = 'https://accounts.recurringvaluepodcast.com';
      return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Check for required permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    console.log(`[ProtectedRoute] Path: ${location.pathname} - No permission, redirecting to unauthorized`);
    return <Navigate to="/unauthorized" replace />;
  }
  
  console.log(`[ProtectedRoute] Path: ${location.pathname} - Rendering children`);
  // Render children if authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;
